import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './FloatingMenu.css';

function FloatingMenu() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [leftBanners, setLeftBanners] = useState([]);
  const [rightBanners, setRightBanners] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    loadFloatingBanners();
    loadRecentProducts();
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 300);
      
      // 130px 이상 스크롤 시 스티키 적용 (초기 top 위치 주변)
      setIsSticky(scrollY > 130);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 플로팅 배너 로드
  const loadFloatingBanners = async () => {
    try {
      const response = await api.get('/public/banners/position/floating').catch(() => ({ data: [] }));
      const banners = response.data || [];
      
      // 좌측 배너 (최대 6개)
      const left = banners
        .filter(b => b.floatingPosition === 'LEFT')
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .slice(0, 6);
      
      // 우측 배너 (최대 3개)
      const right = banners
        .filter(b => b.floatingPosition === 'RIGHT')
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .slice(0, 3);
      
      setLeftBanners(left);
      setRightBanners(right);
    } catch (error) {
      // silently ignore
    }
  };

  // 최근 본 상품 로드
  const loadRecentProducts = () => {
    try {
      const recent = JSON.parse(localStorage.getItem('recentProducts') || '[]');
      setRecentProducts(recent.slice(0, 5)); // 최대 5개
    } catch (error) {
      // silently ignore
    }
  };

  // 위로 가기
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 배너 클릭
  const handleBannerClick = (banner) => {
    if (banner.linkUrl) {
      if (banner.linkUrl.startsWith('http')) {
        window.open(banner.linkUrl, '_blank');
      } else {
        navigate(banner.linkUrl);
      }
    }
  };

  return (
    <>
      {/* 좌측 플로팅 메뉴 */}
      <div className={`floating-menu floating-left ${isSticky ? 'sticky' : ''}`}>
        {leftBanners.map((banner, index) => (
          <div 
            key={banner.id || index} 
            className="floating-banner-item"
            onClick={() => handleBannerClick(banner)}
            style={{ cursor: banner.linkUrl ? 'pointer' : 'default' }}
          >
            <img 
              src={banner.imageUrl} 
              alt={banner.title || `배너 ${index + 1}`}
              className="floating-banner-image"
            />
          </div>
        ))}
      </div>

      {/* 우측 플로팅 메뉴 */}
      <div className={`floating-menu floating-right ${isSticky ? 'sticky' : ''}`}>
        {/* 우측 배너 */}
        {rightBanners.map((banner, index) => (
          <div 
            key={banner.id || index} 
            className="floating-banner-item"
            onClick={() => handleBannerClick(banner)}
            style={{ cursor: banner.linkUrl ? 'pointer' : 'default' }}
          >
            <img 
              src={banner.imageUrl} 
              alt={banner.title || `배너 ${index + 1}`}
              className="floating-banner-image"
            />
          </div>
        ))}

        {/* 최근 본 상품 */}
        {recentProducts.length > 0 && (
          <div className="floating-recent-products">
            <div className="floating-section-title">최근 본 상품</div>
            <div className="recent-products-list">
              {recentProducts.map((product, index) => (
                <Link 
                  key={product.id || index} 
                  to={`/products/${product.id}`}
                  className="recent-product-item"
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="recent-product-image"
                    />
                  ) : (
                    <div className="recent-product-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', fontSize: '10px', color: '#9ca3af', textAlign: 'center', lineHeight: 1.2 }}>
                      <span>등록<br/>예정</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
            <div className="recent-product-count">
              {recentProducts.length}
            </div>
          </div>
        )}

        {/* 퀵 메뉴 버튼들 */}
        <div className="floating-quick-buttons">
          <Link to="/cart" className="floating-quick-btn" title="장바구니">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5.48C20.96 5.34 21 5.17 21 5C21 4.45 20.55 4 20 4H5.21L4.27 2H1ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18Z" fill="currentColor"/>
            </svg>
            <span>장바구니</span>
          </Link>

          <Link to={isAuthenticated ? "/mypage?tab=orders" : "/login"} className="floating-quick-btn" title="주문조회">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" fill="currentColor"/>
            </svg>
            <span>주문조회</span>
          </Link>

          <Link to="/inquiry" className="floating-quick-btn" title="고객센터">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM13 14H11V12H13V14ZM13 10H11V6H13V10Z" fill="currentColor"/>
            </svg>
            <span>문의하기</span>
          </Link>

          {showScrollTop && (
            <button 
              onClick={scrollToTop} 
              className="floating-quick-btn floating-top-btn" 
              title="위로 가기"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 14L12 9L17 14H7Z" fill="currentColor"/>
              </svg>
              <span>TOP</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default FloatingMenu;

